import React from "react";
import { AutoComplete, Form, Input, InputNumber, Select, Switch, Space, Button, Tooltip, Typography } from "antd";
import { FONT } from "../../../styles/typography";

/**
 * 依註冊表的 type 渲染單一設定欄位。
 *
 * 這個元件不認識任何設定的語意 —— 它只認得型別，以及「欄位可以帶一顆動作按鈕」
 * 這個通則。新增一個設定不需要動它。
 */
export default function SettingField({ item, value, onChange, onClear, suggestions, onRunAction, actionRunning, actionDisabledReason, dynamicOptions, onLoadOptions }) {
  // 提示裡的網址附複製鈕，但刻意不做成連結：目前唯一會出現的是 OAuth 回呼網址，
  // 它是要貼進 Google Console 的，手動選取容易漏字，而漏字的結果是授權時的
  // redirect_uri_mismatch —— 錯誤發生在 Google 那邊，很難聯想回這一格。
  // 做成連結則會讓人點進真正的回呼端點 (state 驗不過、留下一筆告警)，對使用者沒有用處。
  const renderHint = (text) =>
    text.split(/(https?:\/\/[^\s，。、]+)/).map((part, i) =>
      /^https?:\/\//.test(part) ? (
        <Typography.Text
          key={i}
          // 專案沒有掛 antd 的 ConfigProvider locale，預設提示是英文的 Copy／Copied
          copyable={{ tooltips: ["複製", "已複製"] }}
          style={{ fontSize: FONT.caption }}
        >
          {part}
        </Typography.Text>
      ) : (
        part
      )
    );

  const hint = item.hint ? (
    <Typography.Text type="secondary" style={{ fontSize: FONT.caption }}>{renderHint(item.hint)}</Typography.Text>
  ) : null;

  let control;

  switch (item.type) {
    case "bool":
      // antd Switch 的 onChange 會多帶一個 event 參數，
      // 這裡只轉發布林值，呼叫端不需要處理事件物件
      control = <Switch checked={!!value} onChange={(checked) => onChange(checked)} />;
      break;
    case "int":
      control = <InputNumber value={value} onChange={onChange} style={{ width: 220 }} />;
      break;
    case "enum":
      control = (
        <Select value={value} onChange={onChange} style={{ width: 220 }}
          options={Object.entries(item.options || {}).map(([v, label]) => ({ value: v, label }))} />
      );
      break;
    case "secret":
      // 金鑰本體永遠不會從後端回來，所以輸入框一律留空。
      // 留空送出代表「不變更」，要清除必須按清除鈕走 DELETE。
      control = (
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          <Space>
            <Input.Password
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              placeholder={item.is_set ? "留空表示不變更" : "尚未設定"}
              // 比其他欄位窄：secret 永遠只顯示「留空表示不變更」，沒有長值可看，
              // 而留下的空間剛好讓動作按鈕能並排在旁邊 (實測欄位可用寬 427px，
              // 260 + 間距 + 按鈕 127 = 395)。窄螢幕仍會由外層的 wrap 換行。
              style={{ width: 260 }}
              autoComplete="new-password"
            />
            {/* 清除鈕必須看 is_overridden (資料庫是否真的存了這一列)，不能看 is_set
                (解析後有沒有生效值，包含 config() 回落) —— 若看 is_set，settings 表
                是空的環境 (例如值全部還在 .env) 會顯示清除鈕，按下去對 0 列做 DELETE，
                回報成功但畫面上的值完全沒變，形同假回饋。 */}
            {item.is_overridden && <Button size="small" danger onClick={onClear}>清除</Button>}
          </Space>
          {item.is_set && (
            <Typography.Text type="secondary" style={{ fontSize: FONT.caption }}>
              目前已設定 {item.value_hint}
            </Typography.Text>
          )}
        </Space>
      );
      break;
    default: {
      // 選項有兩個來源：註冊表裡的靜態建議 (suggestions)，以及要向端點查的
      // 動態選項 (options_from)。兩者都用 AutoComplete —— 刻意不用 Select：
      // 模型代號這類值變動頻繁、據點清單也可能查不到 (尚未授權)，
      // 固定選單會讓人連手動貼上都做不到。
      const dynamic = item.options_from ? dynamicOptions || { loading: false, options: [] } : null;
      const choices = dynamic
        ? (dynamic.options || []).map((o) => ({ value: o.value, label: o.label }))
        : (suggestions || []).map((s) => ({ value: s }));

      control = dynamic || choices.length ? (
        <AutoComplete
          // 完整值放進 title：加寬到吃滿欄位後仍不夠 (實測 OAuth Client ID 要
          // 518px、據點名稱要 464px，可用寬度 427px)，再加寬就得犧牲說明文字的
          // 可讀行寬。使用者要的是核對有沒有貼錯，滑鼠停留看得到就夠了。
          title={value || ""}
          value={value || ""}
          // antd AutoComplete 承襲 Select 的簽章，onChange 會多帶第二個 option
          // 參數 (同下面 Switch 的情況)，這裡只轉發值本身
          onChange={(v) => onChange(v)}
          options={choices}
          onDropdownVisibleChange={(open) => open && onLoadOptions && onLoadOptions()}
          notFoundContent={dynamic && dynamic.loading ? "載入中…" : "沒有可選的項目"}
          style={{ width: "100%" }}
          filterOption={(input, option) => {
            // 帶著已選定的值展開下拉時，輸入框裡的內容是那個值而不是搜尋字。
            // 照常過濾的話清單只會剩下自己那一個 —— 有多個據點時等於換不了店。
            if (choices.some((choice) => choice.value === input)) return true;

            return `${option.value} ${option.label || ""}`.toLowerCase().includes(input.toLowerCase());
          }}
        />
      ) : (
        // 吃滿可用寬度：OAuth Client ID (74 字元) 與據點名稱 (60 字元) 在固定
        // 320px 下只看得到前半段，而這兩個值最需要用眼睛核對有沒有貼錯。
        // secret 不跟著改 —— 它永遠只顯示「留空表示不變更」，沒有長值可看。
        <Input title={value || ""} value={value || ""} onChange={(e) => onChange(e.target.value)} style={{ width: "100%" }} />
      );
      break;
    }
  }

  // 動作按鈕與輸入框並排、上緣對齊：按鈕取得的就是這一格的值，
  // 放在分組底部的按鈕列看不出兩者的關係。align="start" 是必要的 ——
  // secret 型別的控制項是一疊 (輸入框、清除鈕、已設定提示)，
  // 不對齊上緣的話按鈕會被推到那一疊的垂直中線上。
  const actionButton = item.action ? (
    <Tooltip title={actionDisabledReason || null}>
      <Button
        loading={actionRunning === item.action.endpoint}
        disabled={!!actionDisabledReason}
        onClick={() => onRunAction && onRunAction(item.action)}
      >
        {item.action.label}
      </Button>
    </Tooltip>
  ) : null;

  return (
    <Form.Item label={item.label} style={{ marginBottom: 16 }}>
      {actionButton ? (
        // wrap 與 width 100% 必須成對出現：antd 的 Space 預設是 inline-flex，
        // 寬度由內容決定，沒有寬度上限時 flex-wrap 永遠不會觸發。實測 390px 下
        // 少了 width 100%，按鈕右緣落在 483px、被祖先裁掉只露出左緣約 30px，
        // 使用者看不到也按不到；而 document 的 scrollWidth 不會變大，
        // mobileAudit 的溢出量測對這種「被裁切」是盲的，抓不到它。
        <Space align="start" wrap style={{ width: "100%" }}>
          {control}
          {actionButton}
        </Space>
      ) : (
        control
      )}
      {hint && <div style={{ marginTop: 4 }}>{hint}</div>}
    </Form.Item>
  );
}
